/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useModalBackdropPadding } from '../../../shared/ui/hooks-ui/useModalBackdropPadding';
import { searchProducts } from '../../../catalog/infra-api'
import type { ProductSearchRow } from '../../../catalog/domain-api';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import { styles } from '../ReportsScreen.styles';
import { ticketColors } from '../../../shared/theme/ticketColors';

type Props = {
  visible: boolean;
  branchId: string | null;
  onClose: () => void;
  onSelect: (product: Pick<ProductSearchRow, 'id' | 'name' | 'sku'>) => void;
};

export function ReportsVentasProductCatalogPickerModal({ visible, branchId, onClose, onSelect }: Props) {
  const modalBackdropPadding = useModalBackdropPadding();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProductSearchRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => {
      void (async () => {
        setLoading(true);
        try {
          const rows = await searchProducts({
            search: query.trim(),
            limit: 48,
            offset: 0,
            branchId,
          });
          setResults(rows);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      })();
    }, 280);
    return () => clearTimeout(t);
  }, [visible, query, branchId]);

  useEffect(() => {
    if (!visible) {
      setQuery('');
      setResults([]);
    }
  }, [visible]);

  return (
    <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose}>
      <Pressable style={[styles.modalBackdrop, modalBackdropPadding]} onPress={onClose}>
        <Pressable
          style={[styles.modalCard, { maxWidth: 560, width: '100%', alignSelf: 'center' as const }]}
          onPress={(e) => e.stopPropagation()}
        >
          <View style={styles.modalHeader}>
            <View style={styles.ventasCatalogModalTitleRow}>
              <View style={styles.ventasCatalogModalTitleIcon} accessibilityElementsHidden>
                <Ionicons name="pricetags-outline" size={22} color={ticketColors.gold} />
              </View>
              <View style={styles.ventasCatalogModalTitleTextBlock}>
                <Text style={styles.ventasCatalogModalTitleMain}>Elegí un ítem</Text>
                <Text style={styles.ventasCatalogModalTitleSub}>del catálogo de la sucursal</Text>
              </View>
            </View>
            <TouchableOpacity onPress={onClose} accessibilityRole="button" accessibilityLabel="Cerrar" hitSlop={12}>
              <Ionicons name="close" size={26} color={ticketColors.ink} />
            </TouchableOpacity>
          </View>
          <Text style={[styles.caption, { marginBottom: spacing.sm, color: ticketColors.ink3 }]}>
            Al elegir uno, los indicadores y el listado del período muestran solo las ventas de ese producto o servicio.
          </Text>
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Nombre o código"
            placeholderTextColor={ticketColors.ink3}
            style={styles.salesSearchInput}
            accessibilityLabel="Buscar en el catálogo local"
            autoCorrect={false}
            autoCapitalize="none"
          />
          {loading ? (
            <View style={{ paddingVertical: spacing.lg, alignItems: 'center' }}>
              <ActivityIndicator color={ticketColors.ink2} accessibilityLabel="Buscando" />
            </View>
          ) : (
            <FlatList
              data={results}
              keyboardShouldPersistTaps="handled"
              style={styles.salesModalScroll}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => {
                const sku = item.sku ? String(item.sku) : '';
                return (
                  <TouchableOpacity
                    style={[styles.salesListRow, { paddingVertical: spacing.sm }]}
                    onPress={() => onSelect(item)}
                    accessibilityRole="button"
                    accessibilityLabel={sku ? `${item.name}, SKU ${sku}` : item.name}
                  >
                    <View style={styles.salesListRowLeft}>
                      <Text style={styles.salesListRowProducts} numberOfLines={2}>
                        {item.name}
                      </Text>
                      {sku ? <Text style={styles.salesListRowRef}>SKU · {sku}</Text> : null}
                    </View>
                    <Ionicons name="chevron-forward" size={18} color={ticketColors.ink3} />
                  </TouchableOpacity>
                );
              }}
              ListEmptyComponent={
                !loading ? (
                  <Text style={[styles.caption, { paddingVertical: spacing.md, color: ticketColors.ink3 }]}>
                    Sin resultados. Probá otro texto o revisá que el ítem esté en el catálogo de esta sucursal.
                  </Text>
                ) : null
              }
            />
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}
